import { useEffect, useMemo } from "react";

import { useHistory } from "react-router-dom";

import { useGetOwnerDetails } from "~/v2/api/hooks";
import type { Account, Product } from "~/v2/api/rest/account";
import { FeatureFlags, useFeatureEnabled } from "~/v2/helpers/featureFlags";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import {
  type UseLegacyAddWhitelistAddressReturn,
  useLegacyAddWhitelistAddress,
} from "../coinTransfers/legacyAddWhitelistAddress";

export interface DigitalAssetTransfersControllerReturn {
  legacyAddWhitelistAddress: UseLegacyAddWhitelistAddressReturn;
  account: Account | undefined;
  accountId: string;
  product: Product | undefined;
  isLoading: boolean;
  isFetchingEnabled: boolean;
  isTRCEnabled: boolean;
  onAddWhitelistAddressClicked: () => void;
}

export const useDigitalAssetTransfersController =
  (): DigitalAssetTransfersControllerReturn => {
    const history = useHistory();
    const isTRCEnabled = useFeatureEnabled(FeatureFlags.CRYPTO_TRC);

    const { product, currentAccount } = useAccountContext({ liveOnly: true });
    const accountId = currentAccount?.id ?? "";

    const { details } = useGetOwnerDetails();

    const isAccountLoading = !currentAccount || !details;
    const isAccountActive = currentAccount?.status === "ACTIVE";

    const isFetchingEnabled = useMemo(
      () => !isAccountLoading && isAccountActive,
      [isAccountLoading, isAccountActive],
    );

    const legacyAddWhitelistAddress = useLegacyAddWhitelistAddress({
      accountId,
      isFetchingEnabled,
    });

    useEffect(() => {
      if (isAccountLoading) {
        return;
      }
      if (!isAccountActive) {
        history.replace(Path.ROUTE_DASHBOARD);
      }
    }, [history, isAccountActive, isAccountLoading]);

    const onAddWhitelistAddressClicked = () => {
      if (isTRCEnabled) {
        history.push(Path.ROUTE_DIGITAL_ASSETS_WHITELIST_ADDRESS);
        return;
      }

      legacyAddWhitelistAddress.onOpen();
    };

    return {
      isLoading: isAccountLoading,
      legacyAddWhitelistAddress,
      account: currentAccount,
      accountId,
      product,
      isFetchingEnabled,
      isTRCEnabled,
      onAddWhitelistAddressClicked,
    };
  };
